import { HttpStatusCode } from "#/utils/http-status-code";
import type { ApiTokenAuthRequestBody } from "#next/pages/api/auth.api";
import retry from "async-retry";
import { getApiUrl } from "#/constants/environment";
import { z } from "zod";
import { Logger } from "#/lib/logging";


const authApiTokenResponseSchema = z.object({
  message: z.string(),
  user: z.object({
    id: z.string(),
    email: z.string(),
    accessToken: z.string(),
    jwt: z.string(),
    domain: z.string(),
    exp: z.number()
  })
});

export interface AuthApiTokenError {
  error: string,
  details?: Array<{ path: string, message: string }>
}
export type AuthApiTokenResponse = z.infer<typeof authApiTokenResponseSchema>;
/**
 * Exchanges an API token for a JWT session
 * @param apiToken The API token to authenticate with
 * @returns The authentication response with user data
 * @throws Error if the request fails
 */
export const authenticateWithApiToken = async ({
  apiToken
}: ApiTokenAuthRequestBody): Promise<AuthApiTokenResponse> => {
  const url = `${getApiUrl()}/api/auth`;
  // console.log(`Authenticating with API token at ${url} with token ${apiToken}`);
  return retry(async () => {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ apiToken })
    });

    if (!response.ok) {
      let errorData: AuthApiTokenError;
      try {
        errorData = await response.json();
        console.error(`Error response: ${JSON.stringify(errorData)}`);
      } catch {
        throw Logger.error({ error: new Error(`Authentication failed: ${response.statusText}`) });
      }

      if (response.status === HttpStatusCode.BAD_REQUEST) {
        throw Logger.error({ error: new Error(
          errorData.details
            ? `Validation error: ${errorData.details.map((d) => d.message).join(", ")}`
            : errorData.error
        ) });
      } else if (response.status === HttpStatusCode.UNAUTHORIZED) {
        throw Logger.error({ error: new Error("Invalid API token") });
      } else {
        throw Logger.error({ error: new Error(errorData.error || "Authentication failed") });
      }
    }

    const res = await response.json();
    const {
      data, error, success
    } = await authApiTokenResponseSchema.safeParseAsync(res);
    if (!success || error || !data) {
      console.error(`Error parsing response: ${JSON.stringify(res)}`);
      throw Logger.error({
        error,
        message: "Invalid response from authentication API",
        metadata: { res }
      });
    }
    // console.log(`Authentication successful: ${JSON.stringify(data)}`);
    return data;
  }, {
    retries: 5, // Increase max retries
    factor: 1.5, // Exponential factor
    minTimeout: 1000, // Start with 1 second
    maxTimeout: 64000 // Max delay of 64 seconds
  });

};
